import React from "react";
import logo from "../../img/logoWithTextBlue.svg";

interface FolderCatalogViewProps {
  viewedFolder: any;
  withPrices: boolean;
  isAdmin: boolean;
  user: any;
}

const FolderCatalogView: React.FC<FolderCatalogViewProps> = ({ viewedFolder, withPrices, isAdmin, user }) => {
  const canSeeAll = isAdmin || user?.category === "gold";

  const renderBlurredDetails = (content: any) => {
    if (canSeeAll || !content) return content;

    const isPrice = typeof content === "string" && (content.includes("€") || content.includes("$"));
    const placeholder = isPrice ? "XX.XX €" : "Restricted Information";

    return (
      <span style={{ filter: "blur(5px)", userSelect: "none" }}>
        {placeholder}
      </span>
    );
  };

  const getGenderName = (id: any) => {
    switch (parseInt(id)) {
      case 1: return "MEN";
      case 2: return "WOMEN";
      case 3: return "UNISEX";
      case 4: return "KIDS";
      case 5: return "BOYS";
      case 6: return "GIRLS";
      default: return "";
    }
  };

  return (
    <div className="print_catalog_container">
      {viewedFolder.products && viewedFolder.products.map((product: any, idx: number) => (
        <div className="print_page" key={product.id}>
          <div className="print_content_wrapper">
            {/* Images Left - Vertical Column */}
            <div className="images_section">
              <div className="img_container vertical">
                <img 
                  src={process.env.REACT_APP_STORAGE_URL + (product.front_image || (product.images?.[0]?.path))} 
                  alt="Front View" 
                />
                <img 
                  src={process.env.REACT_APP_STORAGE_URL + (product.back_image || (product.images?.[1]?.path || product.images?.[0]?.path))} 
                  alt="Back View" 
                />
              </div>
            </div>

            {/* Details Right */}
            <div className="details_section">
              <div className="print_logo">
                <img src={logo} alt="DEMCO" />
              </div>

              <div className="main_header">
                <h1 className="product_code">{product.code} - {getGenderName(product.gender_id)}</h1>
                <h2 className="product_name">{product.name}</h2>
              </div>

              <p className="description_text">
                {product.description || "High-quality sustainable garment designed for durability and comfort."}
              </p>

              {/* FABRIC SECTION */}
              <div className="data_block">
                <h3>FABRIC</h3>
                {product.fabrics && product.fabrics.length > 0 ? (
                  product.fabric_type === "knit" ? (
                    product.fabrics.map((fabric: any, fIdx: number) => (
                      <div key={fIdx} style={{ marginBottom: fIdx < product.fabrics.length - 1 ? '30px' : '0' }}>
                        <p className="fabric_name" style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>
                          {fabric.fabric_mill?.name} - {fabric.name} {fabric.color && `- ${fabric.color}`}
                        </p>
                        <table className="info_table">
                          <tbody>
                            <tr>
                              <td style={{ width: "33%" }}>
                                <span style={{ fontWeight: "bold" }}>Fabric Type : </span>
                                {fabric.fabric_types?.name || 'Knit'}
                              </td>
                              <td style={{ width: "33%" }}>
                                <span style={{ fontWeight: "bold" }}>Composition : </span>
                                {fabric.composition_value || 'N/A'}
                              </td>
                              <td style={{ width: "33%" }}>
                                <span style={{ fontWeight: "bold" }}>Weight : </span>
                                {fabric.weight_value ? (fabric.weight_value.toLowerCase().includes('oz') ? fabric.weight_value : `${fabric.weight_value} Oz`) : 'No available'}
                              </td>
                            </tr>
                            {withPrices && user?.is_client_clients !== 1 && (
                              <tr>
                                <td style={{ width: "33%" }}>
                                  <span style={{ fontWeight: "bold" }}>Price/kg: </span>
                                  {fabric.price_kg ? renderBlurredDetails(`${fabric.price_kg} €`) : 'No available'}
                                  {fabric.price_metre && (
                                    <>
                                      <br />
                                      <span style={{ fontWeight: "bold" }}>Price/metre : </span>
                                      {renderBlurredDetails(`${fabric.price_metre} €`)}
                                    </>
                                  )}
                                </td>
                                <td style={{ width: "33%" }}>
                                  <span style={{ fontWeight: "bold" }}>Price BCI Cotton: </span>
                                  {fabric.price_bci ? renderBlurredDetails(`${fabric.price_bci} €`) : 'No available'}
                                </td>
                                <td style={{ width: "33%" }}>
                                  <span style={{ fontWeight: "bold" }}>Price Organic cotton : </span>
                                  {fabric.price_organic ? renderBlurredDetails(`${fabric.price_organic} €`) : 'No available'}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    ))
                  ) : (
                    product.fabrics.map((fabric: any, fIdx: number) => (
                      <div key={fIdx} style={{ marginBottom: fIdx < product.fabrics.length - 1 ? '20px' : '0' }}>
                        {withPrices && (
                          <p className="fabric_name">
                            {fabric.fabric_mill?.name} - {fabric.name}
                          </p>
                        )}
                        <table className="info_table">
                          <thead>
                            <tr>
                              <th>Fabric Type : {fabric.fabric_types?.name || 'Denim'}</th>
                              <th>Weight : {fabric.weight_value ? (fabric.weight_value.toLowerCase().includes('oz') ? fabric.weight_value : `${fabric.weight_value} Oz`) : '-'}</th>
                              <th>Elasticity : {fabric.elasticity_value ? `${fabric.elasticity_value} %` : '-'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td colSpan={withPrices ? 2 : 3}>Composition : {fabric.composition_value || 'N/A'}</td>
                              {withPrices && (
                                <td>Unit Price : {fabric.price ? `${Math.fround(fabric.price).toFixed(2)} €` : '-'}</td>
                              )}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ))
                  )
                ) : (
                  <p className="no_data">No Available</p>
                )}
              </div>

              {/* TECHNIQUES SECTION */}
              {product.technics && product.technics.length > 0 && (
                <div className="data_block">
                  <h3>TECHNIQUES</h3>
                  <table className="info_table">
                    <tbody>
                      {product.technics.map((t: any, tIdx: number) => (
                        <tr key={tIdx}>
                          <td style={{ width: withPrices ? '70%' : '100%' }}>
                            Detail : {t.name}
                          </td>
                          {withPrices && (
                            <td>
                              Price : {t.pivot?.technic_price ? `${Math.fround(t.pivot.technic_price).toFixed(2)} €` : '-'}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ACCESSORIES SECTION */}
              {product.accessories && product.accessories.length > 0 && (
                <div className="data_block">
                  <h3>ACCESSORIES</h3>
                  <table className="info_table">
                    <tbody>
                      {product.accessories.map((acc: any, aIdx: number) => (
                        <tr key={aIdx}>
                          <td style={{ width: withPrices ? '70%' : '100%' }}>
                            Accessory Type : {acc.name}
                          </td>
                          {withPrices && (
                            <td>
                              Price : {acc.price ? `${Math.fround(acc.price).toFixed(2)} €` : '-'}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* WASH SECTION */}
              <div className="data_block">
                <h3>WASH</h3>
                <table className="info_table">
                  <tbody>
                    {product.washes && product.washes.slice(0, 1).map((w: any, idx: number) => (
                      <tr key={idx}>
                        <td style={{ width: withPrices ? '35%' : '50%' }}>
                          Type : {w.name}
                        </td>
                        <td style={{ width: withPrices ? '35%' : '50%' }}>
                          Eim Score : {w.pivot?.eim_score || w.eim_score || "25"}
                        </td>
                        {withPrices && (
                          <td>
                            Price : {w.pivot?.wash_price ? `${Math.fround(w.pivot.wash_price).toFixed(2)} €` : '-'}
                          </td>
                        )}
                      </tr>
                    ))}
                    {(!product.washes || product.washes.length === 0) && (
                      <tr>
                        <td>No Available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* TOTAL PRICE BLOCK */}
              {withPrices && (
                <div className="data_block total_price_block" style={{ marginTop: 'auto', borderTop: '2px dashed #00468c', paddingTop: '15px' }}>
                  {product.total_price && (
                    <table className="info_table" style={{ border: 'none' }}>
                      <tbody>
                        <tr style={{ background: '#f0f7ff' }}>
                          <td style={{ border: 'none', fontSize: '18px', fontWeight: 900, color: '#00468c' }}>
                            TOTAL PRICE :
                          </td>
                          <td style={{ border: 'none', fontSize: '20px', fontWeight: 900, color: '#00468c', textAlign: 'right' }}>
                            {renderBlurredDetails(`${Math.fround(product.total_price).toFixed(2)} €`)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FolderCatalogView;
